import { supabase } from "~/integrations/supabase/client";
import type {
  Supplier,
  SupplierFilters,
  SupplierFormData,
} from "~/types/purchasing";
import { rutBody } from "~/utils/helpers";

// RPCs added after the last supabase types regeneration — thin typed wrapper.
const rpcCall = supabase.rpc.bind(supabase) as unknown as <T>(
  fn: string,
  args?: Record<string, unknown>
) => Promise<{ data: T | null; error: { message: string } | null }>;

export const supplierService = {
  async getAll(filters?: SupplierFilters): Promise<Supplier[]> {
    let query = supabase
      .from("suppliers")
      .select("*")
      .order("name", { ascending: true });

    if (filters?.is_active !== undefined) {
      query = query.eq("is_active", filters.is_active);
    }

    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,rut.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as unknown as Supplier[];
  },

  async getById(id: string): Promise<Supplier> {
    const { data, error } = await supabase
      .from("suppliers")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as unknown as Supplier;
  },

  async create(data: SupplierFormData): Promise<Supplier> {
    // Guard against duplicate suppliers by RUT (dedup key = rutBody, ignoring dots/dashes).
    if (data.rut?.trim()) {
      const key = rutBody(data.rut);
      if (key) {
        const { data: existing } = await supabase
          .from("suppliers")
          .select("id, name, rut")
          .not("rut", "is", null);
        const dupe = (existing ?? []).find((s) => rutBody(s.rut) === key);
        if (dupe) {
          throw new Error(`Ya existe un proveedor con este RUT: ${dupe.name}`);
        }
      }
    }

    const { data: supplier, error } = await supabase
      .from("suppliers")
      // cast: master-data columns (razon_social/payment_condition/bank_*) aren't in the
      // generated types yet; the payload is valid at runtime.
      .insert(data as never)
      .select()
      .single();

    if (error) throw error;
    return supplier as unknown as Supplier;
  },

  async update(id: string, data: Partial<SupplierFormData>): Promise<Supplier> {
    const { data: supplier, error } = await supabase
      .from("suppliers")
      .update(data as never)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return supplier as unknown as Supplier;
  },

  async delete(id: string): Promise<void> {
    // Server-side guard: only deletes when the supplier has no movements (RLS-immune RPC).
    const { error } = await rpcCall("delete_supplier", { p_supplier_id: id });
    if (error) throw new Error(error.message);
  },

  /** Supplier ids that have at least one movement (POs, opening balances, prices) — can't be deleted. */
  async getMovementSupplierIds(): Promise<string[]> {
    const { data, error } = await rpcCall<{ supplier_id: string }[]>(
      "get_suppliers_with_movements"
    );
    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => r.supplier_id);
  },

  async toggleActive(id: string, is_active: boolean): Promise<Supplier> {
    return this.update(id, { is_active });
  },
};
